/**
 * #758 — the row's activity summary, made navigable.
 *
 * A spread/total row aggregates the trader's contracts and resting orders
 * across every line in the ladder and shows two rings for them. Until now the
 * lines themselves were plain text: the row could tell you that you were long
 * something at +10.5 while the three-chip strip sat on −2.5, and the only way
 * across was the arrows, one line at a time. On a college-football game that
 * ladder runs past thirty lines.
 *
 * This is one button, and a list behind it. Every line the trader has anything
 * on is in the list, contracts and working orders are told apart, and choosing
 * one selects that exact market in the strip.
 *
 * ─── Why a disclosure rather than chips in the row ─────────────────────────
 *
 * Measured on the real board at 1470×956 before writing any of this: the
 * dashboard's spread/total region already overflows its column by **286px**
 * (1370px of content in 1084px). The row has no width to give. A fixed
 * trigger costs the same whether the trader has one active line or twelve,
 * where a chip per line grows exactly when a trader has most at stake — the
 * shape #650 had to undo on the ladder one ticket ago.
 *
 * This does depart from #650's own compromise (two inline, then `+N`), and the
 * reason is the contract rather than the pixels: the ticket requires the list
 * to *distinguish position from open order*. A chip can carry a line value and
 * nothing else, so a line where the trader holds contracts AND has an order
 * working cannot say so in a chip — but says it plainly in a list row.
 *
 * ─── What is deliberately NOT here ─────────────────────────────────────────
 *
 * No prices, no averages, no P&L. The 'C' ring's hover already carries the
 * per-line entry basis from a single source (`heldContractsTitle`), and a
 * second copy here would be a second place for it to be right. This list
 * answers "which line, and what have I got there" — enough to choose — and
 * owns no number a money surface also owns.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/infra/utils';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import {
  activitySummaryText,
  isReachable,
  type StripActivityLine,
} from './stripActivity';

const PANEL_WIDTH = 232;
const VIEWPORT_MARGIN = 8;
const MIN_PANEL_HEIGHT = 80;
/** Panel padding plus the heading row. */
const PANEL_CHROME = 30;
/** One list row at the pinned leading. */
const PANEL_ROW = 22;

export interface StripActivityMenuProps {
  /** Every line carrying activity, ascending, unresolved last. */
  lines: readonly StripActivityLine[];
  /**
   * The chip label for a line.
   *
   * Passed in rather than derived here, and that is load-bearing: the caller
   * reads it off the CHIP the entry navigates to, so the label in this list
   * and the label on the chip are the same string by construction. Deriving it
   * locally would reintroduce two spellings of one line — which the row was
   * already doing, rendering `−3.5` (U+2212) beside a chip reading `-3.5`
   * (ASCII), and would silently disagree again the moment the ⇄ perspective
   * flip negated the chips and not this list.
   */
  labelFor: (line: StripActivityLine) => string;
  /** 'Spread' | 'Total' — names the strip in the accessible labels. */
  kindLabel: string;
  /**
   * Select the line with this TICKER. Never called for an unreachable row.
   *
   * A ticker, not the index this row already knows, because the ticket says so
   * in as many words — *"Identity is by exact ticker/line key, never array
   * index or display text"* — and because the index is only correct for as
   * long as the array it indexes is unchanged. The list can sit open while the
   * feed adds or drops a line; resolving the ticker at CLICK time means a
   * vanished line selects nothing, where an index would quietly select
   * whichever market had shifted into that slot. That is the #141 bug in a
   * different costume.
   */
  onSelect: (ticker: string) => void;
  /**
   * #648/#758 — take a FREE hotkey focus, exactly as a chip click does.
   *
   * Choosing a line here and choosing it in the strip are the same act on the
   * same card, so they have to leave the trader in the same state. Without
   * this they did not: `MiniLineStrip` calls `focusIfFree` after every chip
   * select, so picking +10.5 from the strip armed A/D on this card while
   * picking the identical line from this list left the focus wherever it was.
   * Same market, same gesture, two different hotkey targets.
   *
   * `focusIfFree` never steals a focus another card holds and never clears
   * one, so this cannot disturb a trader working elsewhere on the board.
   */
  onFocusCard?: () => void;
}

export function StripActivityMenu({
  lines,
  labelFor,
  kindLabel,
  onSelect,
  onFocusCard,
}: StripActivityMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 0 });

  /**
   * Activity drains while the panel is open — an order fills, a position
   * settles — and an empty list floating over the board with nothing that
   * opened it is worse than no panel. Dropped during render rather than in an
   * effect, following `RestingOrderGroup`: an effect runs a tick late and
   * leaves one frame of a stale panel that is still clickable.
   */
  if (open && lines.length === 0) setOpen(false);

  const reposition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Left-aligned to the trigger — this button sits in the row's FIRST column,
    // where right-aligning (RestingOrderGroup's choice, for a control at the
    // right edge of a ladder) would throw the panel back across the row it
    // belongs to. Clamped either way so it cannot leave the viewport.
    const rightLimit = Math.max(
      VIEWPORT_MARGIN,
      window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN,
    );
    const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), rightLimit);

    // Prefer below; flip above when the list would fit there and not below.
    // Spread/total rows sit low on a full board as often as high, and a list
    // whose tail is under the fold is reachable, not visible.
    const wanted = PANEL_CHROME + lines.length * PANEL_ROW;
    const roomBelow = window.innerHeight - rect.bottom - 4 - VIEWPORT_MARGIN;
    const roomAbove = rect.top - 4 - VIEWPORT_MARGIN;

    let top: number;
    if (wanted <= roomBelow || roomBelow >= roomAbove) {
      top = Math.max(VIEWPORT_MARGIN, rect.bottom + 4);
    } else {
      top = Math.max(VIEWPORT_MARGIN, rect.top - 4 - Math.min(wanted, roomAbove));
    }
    if (window.innerHeight - top - VIEWPORT_MARGIN < MIN_PANEL_HEIGHT) {
      top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - MIN_PANEL_HEIGHT);
    }
    const maxHeight = window.innerHeight - top - VIEWPORT_MARGIN;
    setPos((prev) =>
      prev.top === top && prev.left === left && prev.maxHeight === maxHeight
        ? prev
        : { top, left, maxHeight },
    );
  }, [lines.length]);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    // Capture phase: the scrolling element is the table's own overflow-x
    // container, not the window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  /** Close, and hand focus back to the trigger when it was inside the panel.
   *  A keyboard user who pressed Escape would otherwise be dropped onto
   *  <body> — the panel holding their focus unmounts — losing their place in
   *  the board. Only reclaimed when focus was actually inside: a mouse
   *  dismissal elsewhere on the page must not yank focus back here. */
  const close = useCallback(() => {
    const inside = panelRef.current?.contains(document.activeElement);
    setOpen(false);
    if (inside) triggerRef.current?.focus();
  }, []);

  useDismissibleLayer({
    open,
    onDismiss: close,
    refs: [triggerRef, panelRef],
    // The panel is `z-[100]` while atoms/Modal is `z-50`, so without focus-out
    // dismissal it could sit on top of a modal as a background layer. Focus
    // inside the panel counts as inside — both refs are listed — so moving
    // between rows with Tab is safe.
    dismissOnFocusOut: true,
  });

  // The portal appends the panel at the END of <body>, so a Tab from the
  // trigger would walk the entire rest of the page before reaching the list —
  // which would make "every line reachable by keyboard" true only in the most
  // literal sense. Focus moves into the panel on open; Escape hands it back.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (lines.length === 0) return null;

  /**
   * The count plus a disclosure caret, and nothing else.
   *
   * Measured, not chosen by taste: this control sits in the row's first
   * column, which is ~75px and already carrying the strip label and two
   * rings. `2 lines` needed roughly 42px more than fits and WRAPPED — and the
   * usual fix is the wrong one here, because the cell wrapping is what stops
   * the column widening. `whitespace-nowrap` would have traded a tidy label
   * for more overhang on a board already 286px past its column at 1470.
   *
   * So the label shrinks instead. `#650`'s overflow control on this same table
   * is a bare `+2` with no word at all, so a terse disclosure is the house
   * pattern; the caret is what keeps it from reading as a quantity of
   * contracts beside the two rings. The sentence lives in the title and the
   * accessible name, which is where a dense trading row keeps its prose.
   */

  return (
    <span className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Show the ${lines.length} ${kindLabel.toLowerCase()} ${
          lines.length === 1 ? 'line' : 'lines'
        } you have contracts or orders on`}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={`You have contracts or resting orders on ${lines.length} ${kindLabel.toLowerCase()} ${
          lines.length === 1 ? 'line' : 'lines'
        } — click to list them and jump to one`}
        // The row itself is a click target (it expands, and moves the hotkey
        // focus). Every control already living in this cell stops propagation
        // for the same reason; without it, opening the list would also toggle
        // the row underneath.
        onClick={(e) => {
          e.stopPropagation();
          if (open) close();
          else setOpen(true);
        }}
        className={cn(
          'text-muted-foreground hover:text-foreground shrink-0 rounded px-0.5',
          'font-mono text-[10px] tabular-nums leading-4 tracking-tight transition-colors',
          'whitespace-nowrap',
          'focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2',
          open && 'bg-muted/60 text-foreground',
        )}
      >
        {lines.length}
        <span aria-hidden className="ml-px">▾</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            // Non-modal on purpose: `Lt2HotkeyListener`'s modal query is
            // `[role="dialog"][aria-modal="true"]`, so a modal here would
            // suppress the A/D trading hotkeys page-wide. Stated rather than
            // left absent, because the portal puts this on <body> where a
            // future modal query is likelier to sweep it up.
            role="dialog"
            aria-modal="false"
            aria-label={`${kindLabel} lines you have contracts or orders on`}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight, width: PANEL_WIDTH }}
            className={cn(
              'bg-card border-border fixed z-[100] overflow-y-auto rounded-lg border',
              'flex flex-col gap-0.5 p-1.5 shadow-lg',
              'text-[10px] font-normal leading-4 focus:outline-none',
            )}
          >
            <div className="text-muted-foreground mb-1 whitespace-nowrap px-0.5 text-[9px] font-semibold uppercase tracking-wider">
              {kindLabel} — your lines
            </div>
            {lines.map((line) => {
              const reachable = isReachable(line);
              const summary = activitySummaryText(line);
              const held = (line.position?.yesCount ?? 0) + (line.position?.noCount ?? 0) > 0;
              const working = line.ordersYes + line.ordersNo > 0;
              return (
                <button
                  key={line.ticker}
                  type="button"
                  disabled={!reachable}
                  // A real <button>, not a focusable span. `Lt2HotkeyListener`
                  // decides whether a keystroke belongs to a control via
                  // `el.closest('button')` and deliberately does not look at
                  // `role="button"` — so a focusable span here would let a
                  // trader tabbed onto this list press A or D and place an
                  // order from it. The same fence #650 was caught by.
                  onClick={(e) => {
                    e.stopPropagation();
                    if (line.idx == null) return;
                    onSelect(line.ticker);
                    onFocusCard?.();
                    close();
                  }}
                  title={
                    reachable
                      ? `Select ${labelFor(line)} in the line strip`
                      : // The rings beside the trigger already count this
                        // order, so the list has to show it or the two
                        // disagree — but there is no chip to select.
                        `${labelFor(line)} is not listed on this board, so it cannot be selected here`
                  }
                  className={cn(
                    'flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left',
                    'focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2',
                    reachable
                      ? 'hover:bg-muted/60 text-foreground'
                      : 'text-muted-foreground/60 cursor-default',
                  )}
                >
                  {/* Shape carries the KIND, matching the rings this list sits
                      under: a filled dot for contracts held, a hollow ring for
                      a working order. Reusing the row's own vocabulary rather
                      than inventing a second one. */}
                  <span className="flex w-6 shrink-0 items-center gap-0.5" aria-hidden>
                    <span
                      className={cn(
                        'inline-block h-2 w-2 rounded-full',
                        held ? 'bg-primary' : 'opacity-0',
                      )}
                    />
                    <span
                      className={cn(
                        'border-primary inline-block h-2 w-2 rounded-full border',
                        working ? '' : 'opacity-0',
                      )}
                    />
                  </span>
                  <span className="w-[5ch] shrink-0 font-mono tabular-nums">
                    {labelFor(line)}
                  </span>
                  <span className="text-muted-foreground truncate">{summary}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </span>
  );
}
